/**
 * Modal de configuração para nodes de pergunta (botões e lista)
 * Permite editar pergunta, opções de resposta e comportamento de resposta inválida
 * 
 * RELEASE: 4.2.0 - 2026-09-07 - Variável de resposta migrada para o modal de destino.
 * RELEASE: 4.1.0 - 2026-01-25 - Adicionado seletor de canal de envio para suporte a gatilhos CRM
 */

import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { FlowNode, QuestionButtonsConfig, QuestionListConfig, InvalidResponseConfig, InvalidResponseAction } from '@/types/flows';
import { z } from 'zod';
import { toast } from 'sonner';
import { Plus, Trash2, HelpCircle, MessageSquareText, AlertTriangle, ChevronDown, Database, Braces, Send, MessageSquare } from 'lucide-react';
import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { VariablePickerCombobox } from '../VariablePickerCombobox';
import { VariableDestinationPicker } from '../VariableDestinationPicker';
import { TextareaWithVariables } from '../TextareaWithVariables';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Schema de validação para pergunta
const questionSchema = z.object({
  text: z.string().trim().min(1, 'O texto da pergunta é obrigatório').max(1024, 'Texto muito longo'),
  options: z.array(z.object({
    id: z.string(),
    label: z.string().trim().min(1, 'Label é obrigatório').max(100, 'Label muito longo'),
    title: z.string().trim().max(100).optional(),
    description: z.string().trim().max(200).optional(),
  })).min(1, 'Adicione pelo menos uma opção').max(10, 'Máximo de 10 opções'),
});

interface ConfigureQuestionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  nodes?: FlowNode[]; // Lista de todos os nodes para seleção de goto
  onSave: (nodeId: string, config: QuestionButtonsConfig | QuestionListConfig) => void;
  tenantId: string;
}

/**
 * Dialog de configuração de perguntas
 */
export function ConfigureQuestionDialog({ open, onOpenChange, node, nodes = [], onSave, tenantId }: ConfigureQuestionDialogProps) {
  const isList = node?.data.type === 'question_list';
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  
  const [variableId, setVariableId] = useState('');
  const [variableName, setVariableName] = useState(node?.data.config?.variable_name || '');
  const [text, setText] = useState(node?.data.config?.text || '');
  const [options, setOptions] = useState<Array<{ id: string; label: string; title?: string; description?: string }>>(
    node?.data.config?.options || [{ id: '1', label: '', title: '', description: '' }]
  );
  const [fallbackEnabled, setFallbackEnabled] = useState(node?.data.config?.fallback_enabled || false);
  const [showHelp, setShowHelp] = useState(false);
  
  // Estados para lista dinâmica (apenas para question_list)
  // RELEASE: 2.2.0 - 2024-12-28 - Adicionado optionsArrayPath para navegação em objetos aninhados
  const [optionsSource, setOptionsSource] = useState<'static' | 'variable'>(
    node?.data.config?.options_source || 'static'
  );
  const [optionsVariable, setOptionsVariable] = useState(node?.data.config?.options_variable || '');
  const [optionsArrayPath, setOptionsArrayPath] = useState(node?.data.config?.options_array_path || '');
  const [optionsLabelPath, setOptionsLabelPath] = useState(node?.data.config?.options_label_path || '');
  const [optionsValuePath, setOptionsValuePath] = useState(node?.data.config?.options_value_path || '');
  const [optionsDescriptionPath, setOptionsDescriptionPath] = useState(node?.data.config?.options_description_path || '');
  const [optionsDistinct, setOptionsDistinct] = useState(node?.data.config?.options_distinct ?? true);
  
  // Estados para resposta inválida
  const [invalidResponseOpen, setInvalidResponseOpen] = useState(false);
  const [invalidResponseEnabled, setInvalidResponseEnabled] = useState(
    node?.data.config?.invalid_response?.enabled || false
  );
  const [invalidResponseAction, setInvalidResponseAction] = useState<InvalidResponseAction>(
    node?.data.config?.invalid_response?.action || 'message_then_repeat'
  );
  const [invalidResponseMessage, setInvalidResponseMessage] = useState(
    node?.data.config?.invalid_response?.message || 'Opção inválida! Por favor, escolha uma das opções disponíveis.'
  );
  const [invalidResponseMaxRetries, setInvalidResponseMaxRetries] = useState(
    node?.data.config?.invalid_response?.max_retries || 3
  );
  const [invalidResponseExceededAction, setInvalidResponseExceededAction] = useState<'continue' | 'end' | 'goto_node'>(
    node?.data.config?.invalid_response?.exceeded_action || 'end'
  );
  
  // RELEASE: 4.1.0 - Estados para canal de envio
  const [channelMode, setChannelMode] = useState<'active' | 'specific'>('active');
  const [selectedInboxId, setSelectedInboxId] = useState<string>('');

  /**
   * Query para buscar inboxes ativas do tenant
   * RELEASE: 4.1.0 - Permite selecionar canal específico para envio
   */
  const { data: inboxes = [], isLoading: loadingInboxes } = useQuery({
    queryKey: ['inboxes-selector-question', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inboxes')
        .select('id, name, channel_type, identifier')
        .eq('tenant_id', tenantId)
        .eq('status', 'active')
        .order('name');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId && open,
  });

  /**
   * Sincroniza estados quando node mudar
   */
  useEffect(() => {
    if (node) {
      // Reset do variableId - CORRIGIDO: agora reseta corretamente
      setVariableId(node.data.config?.variable_id || '');
      setVariableName(node.data.config?.variable_name || '');
      setText(node.data.config?.text || '');
      setOptions(node.data.config?.options || [{ id: '1', label: '', title: '', description: '' }]);
      setFallbackEnabled(node.data.config?.fallback_enabled || false);
      
      // Sincroniza estados de lista dinâmica
      setOptionsSource(node.data.config?.options_source || 'static');
      setOptionsVariable(node.data.config?.options_variable || '');
      setOptionsArrayPath(node.data.config?.options_array_path || '');
      setOptionsLabelPath(node.data.config?.options_label_path || '');
      setOptionsValuePath(node.data.config?.options_value_path || '');
      setOptionsDescriptionPath(node.data.config?.options_description_path || '');
      setOptionsDistinct(node.data.config?.options_distinct ?? true);
      
      // Sincroniza estados de resposta inválida
      const invalidConfig = node.data.config?.invalid_response;
      setInvalidResponseEnabled(invalidConfig?.enabled || false);
      setInvalidResponseAction(invalidConfig?.action || 'message_then_repeat');
      setInvalidResponseMessage(invalidConfig?.message || 'Opção inválida! Por favor, escolha uma das opções disponíveis.');
      setInvalidResponseMaxRetries(invalidConfig?.max_retries || 3);
      setInvalidResponseExceededAction(invalidConfig?.exceeded_action || 'end');
      
      // Abre seção se já estiver configurada
      if (invalidConfig?.enabled) {
        setInvalidResponseOpen(true);
      }
      
      // RELEASE: 4.1.0 - Inicializa canal de envio
      const hasInboxConfig = !!node.data.config?.inbox_id;
      setChannelMode(hasInboxConfig ? 'specific' : 'active');
      setSelectedInboxId(node.data.config?.inbox_id || '');
    }
  }, [node]);
  
  /**
   * Reset estados quando modal fecha
   */
  useEffect(() => {
    if (!open) {
      setVariableId('');
      setVariableName('');
    }
  }, [open]);

  /**
   * Adiciona nova opção
   */
  const addOption = () => {
    if (options.length >= 10) {
      toast.error('Máximo de 10 opções permitidas');
      return;
    }
    setOptions([...options, { id: String(options.length + 1), label: '', title: '', description: '' }]);
  };

  /**
   * Remove uma opção
   */
  const removeOption = (index: number) => {
    if (options.length <= 1) {
      toast.error('É necessário pelo menos uma opção');
      return;
    }
    setOptions(options.filter((_, i) => i !== index));
  };

  /**
   * Atualiza uma opção
   */
  const updateOption = (index: number, field: string, value: string) => {
    const updated = [...options];
    (updated[index] as any)[field] = value;
    setOptions(updated);
  };

  /**
   * Salva a configuração
   */
  /**
   * Salva a configuração do node
   * RELEASE: 4.1.0 - Adiciona inbox_id ao config quando canal específico selecionado
   */
  const handleSave = () => {
    if (!node) return;

    try {
      // RELEASE: 4.1.0 - Validação de canal específico
      if (channelMode === 'specific' && !selectedInboxId) {
        toast.error('Selecione uma inbox para envio');
        return;
      }

      // Validação condicional baseada na fonte de opções
      if (optionsSource === 'static') {
        questionSchema.parse({ text, options });
      } else {
        // Para lista dinâmica, apenas valida o texto e campos obrigatórios
        if (!text.trim()) {
          toast.error('O texto da pergunta é obrigatório');
          return;
        }
        if (!optionsVariable.trim()) {
          toast.error('Selecione a variável que contém as opções');
          return;
        }
        if (!optionsLabelPath.trim()) {
          toast.error('Informe o caminho para o label da opção');
          return;
        }
      }
      
      // Monta configuração de resposta inválida
      const invalidResponseConfig: InvalidResponseConfig | undefined = invalidResponseEnabled ? {
        enabled: true,
        action: invalidResponseAction,
        message: invalidResponseAction === 'message_then_repeat' ? invalidResponseMessage : undefined,
        max_retries: ['repeat', 'message_then_repeat'].includes(invalidResponseAction) ? invalidResponseMaxRetries : undefined,
        exceeded_action: ['repeat', 'message_then_repeat'].includes(invalidResponseAction) ? invalidResponseExceededAction : undefined,
      } : undefined;
      
      // Montar config base - CORRIGIDO: inclui variable_id para persistir seleção
      const config: QuestionButtonsConfig | QuestionListConfig = {
        text: text.trim(),
        options: optionsSource === 'static' ? options : [], // Opções vazias se dinâmica
        variable_id: variableId || undefined,
        variable_name: variableName,
        fallback_enabled: fallbackEnabled,
        invalid_response: invalidResponseConfig,
      };
      
      // Adiciona campos de lista dinâmica apenas para question_list
      if (isList) {
        (config as QuestionListConfig).options_source = optionsSource;
        if (optionsSource === 'variable') {
          (config as QuestionListConfig).options_variable = optionsVariable;
          (config as QuestionListConfig).options_array_path = optionsArrayPath || undefined;
          (config as QuestionListConfig).options_label_path = optionsLabelPath;
          (config as QuestionListConfig).options_value_path = optionsValuePath || optionsLabelPath;
          (config as QuestionListConfig).options_description_path = optionsDescriptionPath || undefined;
          (config as QuestionListConfig).options_distinct = optionsDistinct;
        }
      }
      
      // RELEASE: 4.1.0 - Adiciona inbox_id se canal específico
      if (channelMode === 'specific' && selectedInboxId) {
        (config as any).inbox_id = selectedInboxId;
      }
      
      onSave(node.id, { ...config, nickname: nicknameRef.current?.getNickname() } as any);
      toast.success('Configuração salva com sucesso');
      onOpenChange(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        toast.error(firstError.message);
      } else {
        toast.error('Erro ao salvar configuração');
      }
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>
                {isList ? 'Configurar Lista de Opções' : 'Configurar Pergunta com Botões'}
              </DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
          </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Campo ID do Node (somente leitura) */}
          <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

          {/* RELEASE: 4.1.0 - Seção Canal de Envio */}
          <Separator className="my-2" />
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Send className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Canal de Envio</Label>
            </div>
            <RadioGroup
              value={channelMode}
              onValueChange={(v: 'active' | 'specific') => setChannelMode(v)}
              className="space-y-2"
            >
              <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer">
                <RadioGroupItem value="active" id="q-channel-active" className="mt-0.5" />
                <div className="space-y-1 flex-1">
                  <Label htmlFor="q-channel-active" className="font-medium cursor-pointer">
                    Canal da conversa ativa
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Usa o canal definido pelo gatilho do fluxo (padrão para gatilhos de chat)
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer">
                <RadioGroupItem value="specific" id="q-channel-specific" className="mt-0.5" />
                <div className="space-y-1 flex-1">
                  <Label htmlFor="q-channel-specific" className="font-medium cursor-pointer">
                    Canal específico
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Selecione manualmente o canal (obrigatório para gatilhos CRM)
                  </p>
                </div>
              </div>
            </RadioGroup>

            {/* Seletor de Inbox (apenas quando modo específico) */}
            {channelMode === 'specific' && (
              <div className="space-y-2 pl-4 border-l-2 border-primary/30">
                <Label>Inbox para Envio *</Label>
                <Select 
                  value={selectedInboxId} 
                  onValueChange={setSelectedInboxId}
                  disabled={loadingInboxes}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma inbox..." />
                  </SelectTrigger>
                  <SelectContent>
                    {inboxes.map((inbox) => (
                      <SelectItem key={inbox.id} value={inbox.id}>
                        <div className="flex items-center gap-2">
                          <MessageSquare className="h-3 w-3" />
                          <span>{inbox.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({inbox.channel_type})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  A pergunta será enviada através deste canal
                </p>
              </div>
            )}
          </div>
          <Separator className="my-2" />

          <div className="space-y-2">
            <Label>Variável para Salvar Resposta (opcional)</Label>
            <VariableDestinationPicker
              tenantId={tenantId}
              value={variableId || variableName}
              valueName={variableName}
              onChange={(variable) => {
                if (variable) {
                  setVariableId(variable.id);
                  setVariableName(variable.name);
                } else {
                  // Limpa seleção
                  setVariableId('');
                  setVariableName('');
                }
              }}
              allowClear
              placeholder="Selecione variável para salvar a escolha"
            />
            <p className="text-xs text-muted-foreground">
              A opção escolhida será salva nesta variável
            </p>
          </div>

          <div className="space-y-2">
            <Label>Texto da Pergunta *</Label>
            <TextareaWithVariables
              tenantId={tenantId}
              value={text}
              onChange={setText}
              placeholder="Digite a pergunta... Use {{variavel}} para inserir variáveis"
              maxLength={1024}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">{text.length}/1024 caracteres</p>
          </div>

          {/* Seção de Opções - Com suporte a lista dinâmica para question_list */}
          {isList && (
            <div className="border rounded-lg p-4 space-y-3 bg-muted/20">
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5 text-muted-foreground" />
                <div>
                  <Label className="text-base font-medium">Fonte das Opções</Label>
                  <p className="text-sm text-muted-foreground">
                    Escolha de onde virão as opções da lista
                  </p>
                </div>
              </div>
              
              <Select value={optionsSource} onValueChange={(v) => setOptionsSource(v as 'static' | 'variable')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="static">Opções estáticas (definidas aqui)</SelectItem>
                  <SelectItem value="variable">Opções dinâmicas (de variável)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Configuração de lista dinâmica (apenas quando source = variable) */}
          {isList && optionsSource === 'variable' && (
            <div className="border rounded-lg p-4 space-y-4 bg-gradient-to-br from-primary/5 to-primary/10">
              <div className="flex items-center gap-2">
                <Braces className="h-5 w-5 text-primary" />
                <Label className="text-base font-medium">Configurar Lista Dinâmica</Label>
              </div>
              
              <div className="space-y-2">
                <Label>Variável com Dados *</Label>
                <VariablePickerCombobox
                  tenantId={tenantId}
                  value={optionsVariable}
                  onChange={(variable) => {
                    if (variable) {
                      // Usa o nome da variável para manter compatibilidade com lógica existente
                      setOptionsVariable(variable.name);
                    } else {
                      setOptionsVariable('');
                    }
                  }}
                  allowClear={true}
                  placeholder="Selecione a variável..."
                />
                <p className="text-xs text-muted-foreground">
                  Selecione a variável que contém os dados (geralmente resposta de webhook)
                </p>
              </div>
              
              {/* NOVO: Campo para caminho até o array */}
              <div className="space-y-2">
                <Label>Caminho até o Array (opcional)</Label>
                <Input
                  placeholder="Ex: veiculos, data.items, response.dados.lista"
                  value={optionsArrayPath}
                  onChange={(e) => setOptionsArrayPath(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Se o array está dentro do objeto, informe o caminho usando pontos para cada nível. Deixe vazio se a variável já é o array.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Caminho para o Label *</Label>
                  <Input
                    placeholder="Ex: veiculo_marca"
                    value={optionsLabelPath}
                    onChange={(e) => setOptionsLabelPath(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Campo que será exibido ao usuário
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Caminho para o Value</Label>
                  <Input
                    placeholder="Ex: veiculo_placa"
                    value={optionsValuePath}
                    onChange={(e) => setOptionsValuePath(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Campo que será salvo na variável
                  </p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Caminho para Descrição (opcional)</Label>
                <Input
                  placeholder="Ex: status ou valor_devido"
                  value={optionsDescriptionPath}
                  onChange={(e) => setOptionsDescriptionPath(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Campo adicional para exibir como descrição da opção
                </p>
              </div>
              
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  <strong>💡 Exemplo:</strong> Se a variável <code className="bg-muted px-1 rounded">boletos</code> contém:{' '}
                  <code className="bg-muted px-1 rounded text-xs">[{`{veiculo_marca: "FIAT UNO", veiculo_placa: "ABC-1234"}`}]</code>,
                  configure label como <code className="bg-muted px-1 rounded">veiculo_marca</code> e value como <code className="bg-muted px-1 rounded">veiculo_placa</code>.
                </p>
              </div>
              
              {/* Switch para agrupar valores únicos (distinct) */}
              <div className="flex items-center justify-between py-2 border-t border-border/50 mt-2 pt-3">
                <div className="space-y-0.5">
                  <Label>Agrupar Valores Únicos</Label>
                  <p className="text-xs text-muted-foreground">
                    Remove duplicatas baseado no campo de value
                  </p>
                </div>
                <Switch
                  checked={optionsDistinct}
                  onCheckedChange={setOptionsDistinct}
                />
              </div>
            </div>
          )}

          {/* Opções estáticas (original) - exibe apenas quando não é lista dinâmica */}
          {(!isList || optionsSource === 'static') && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Opções de Resposta *</Label>
                <Button size="sm" variant="outline" onClick={addOption}>
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar Opção
                </Button>
              </div>

              <div className="space-y-3">
                {options.map((option, index) => (
                  <div key={option.id} className="border rounded-lg p-3 space-y-2 bg-muted/30">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Opção {index + 1}</span>
                      {options.length > 1 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeOption(index)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Input
                        placeholder="Label do botão *"
                        value={option.label}
                        onChange={(e) => updateOption(index, 'label', e.target.value)}
                        maxLength={100}
                      />

                      {isList && (
                        <>
                          <Input
                            placeholder="Título (opcional)"
                            value={option.title || ''}
                            onChange={(e) => updateOption(index, 'title', e.target.value)}
                            maxLength={100}
                          />
                          <Input
                            placeholder="Descrição (opcional)"
                            value={option.description || ''}
                            onChange={(e) => updateOption(index, 'description', e.target.value)}
                            maxLength={200}
                          />
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border rounded-lg p-4 space-y-3 bg-muted/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquareText className="h-5 w-5 text-muted-foreground" />
                <div>
                  <Label className="text-base font-medium">Usar Menu Numérico</Label>
                  <p className="text-sm text-muted-foreground">
                    {fallbackEnabled 
                      ? 'Menu numérico será usado (ex: 1️⃣ Opção A, 2️⃣ Opção B)'
                      : 'Botões nativos do WhatsApp serão usados quando disponíveis'
                    }
                  </p>
                </div>
              </div>
              <Switch
                checked={fallbackEnabled}
                onCheckedChange={setFallbackEnabled}
              />
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <p><strong>Botões Nativos (Desligado):</strong> Usa botões interativos do WhatsApp. Mais intuitivo, mas nem todos os providers suportam.</p>
              <p><strong>Menu Numérico (Ligado):</strong> Envia mensagem de texto com opções numeradas. Funciona em todos os providers e permite resposta via digitação.</p>
            </div>
          </div>

          {/* Seção de Resposta Inválida */}
          <Collapsible open={invalidResponseOpen} onOpenChange={setInvalidResponseOpen}>
            <CollapsibleTrigger asChild>
              <div className="border rounded-lg p-4 cursor-pointer hover:bg-muted/30 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    <div>
                      <Label className="text-base font-medium cursor-pointer">Comportamento para Resposta Inválida</Label>
                      <p className="text-sm text-muted-foreground">
                        Configure o que fazer quando o usuário responder fora das opções
                      </p>
                    </div>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${invalidResponseOpen ? 'rotate-180' : ''}`} />
                </div>
              </div>
            </CollapsibleTrigger>
            
            <CollapsibleContent className="border border-t-0 rounded-b-lg p-4 space-y-4 bg-muted/10">
              {/* Toggle habilitar tratamento */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Tratar resposta inválida</Label>
                  <p className="text-xs text-muted-foreground">
                    Habilita tratamento especial quando o usuário responde algo diferente das opções
                  </p>
                </div>
                <Switch
                  checked={invalidResponseEnabled}
                  onCheckedChange={setInvalidResponseEnabled}
                />
              </div>

              {invalidResponseEnabled && (
                <>
                  {/* Seleção de ação */}
                  <div className="space-y-2">
                    <Label>Quando o usuário responder fora das opções:</Label>
                    <Select value={invalidResponseAction} onValueChange={(v) => setInvalidResponseAction(v as InvalidResponseAction)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="repeat">Repetir a pergunta</SelectItem>
                        <SelectItem value="message_then_repeat">Enviar mensagem de erro e repetir</SelectItem>
                        <SelectItem value="continue">Continuar fluxo (ignorar)</SelectItem>
                        <SelectItem value="end">Encerrar fluxo</SelectItem>
                        <SelectItem value="goto_node">Ir para outro node</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Mensagem personalizada (apenas para message_then_repeat) */}
                  {invalidResponseAction === 'message_then_repeat' && (
                    <div className="space-y-2">
                      <Label>Mensagem de erro</Label>
                      <Textarea
                        value={invalidResponseMessage}
                        onChange={(e) => setInvalidResponseMessage(e.target.value)}
                        placeholder="Opção inválida! Por favor, escolha uma das opções disponíveis."
                        maxLength={500}
                        rows={2}
                      />
                      <p className="text-xs text-muted-foreground">
                        Esta mensagem será enviada antes de repetir a pergunta
                      </p>
                    </div>
                  )}

                  {/* Máximo de tentativas (para ações com repetição) */}
                  {['repeat', 'message_then_repeat'].includes(invalidResponseAction) && (
                    <>
                      <div className="space-y-2">
                        <Label>Máximo de tentativas</Label>
                        <Input
                          type="number"
                          min={1}
                          max={10}
                          value={invalidResponseMaxRetries}
                          onChange={(e) => setInvalidResponseMaxRetries(parseInt(e.target.value) || 3)}
                          className="w-24"
                        />
                        <p className="text-xs text-muted-foreground">
                          Após este número de tentativas inválidas, executar ação alternativa
                        </p>
                      </div>

                      {/* Ação após exceder tentativas */}
                      <div className="space-y-2">
                        <Label>Após exceder tentativas:</Label>
                        <Select value={invalidResponseExceededAction} onValueChange={(v) => setInvalidResponseExceededAction(v as 'continue' | 'end' | 'goto_node')}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="continue">Continuar fluxo</SelectItem>
                            <SelectItem value="end">Encerrar fluxo</SelectItem>
                            <SelectItem value="goto_node">Ir para outro node</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Instrução para conectar visualmente após exceder tentativas */}
                      {invalidResponseExceededAction === 'goto_node' && (
                        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                          <p className="text-xs text-blue-700 dark:text-blue-300">
                            <strong>🔗 Conexão visual:</strong> Conecte o handle laranja "Resposta Inválida" 
                            ao node destino no canvas. O sistema seguirá essa conexão após exceder as tentativas.
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {/* Instrução para conectar visualmente (goto_node direto) */}
                  {invalidResponseAction === 'goto_node' && (
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        <strong>🔗 Conexão visual:</strong> Conecte o handle laranja "Resposta Inválida" 
                        ao node destino no canvas. O sistema seguirá essa conexão quando receber uma resposta inválida.
                      </p>
                    </div>
                  )}

                  {/* Dica informativa */}
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      <strong>💡 Dica:</strong> O sistema repetirá a pergunta até o usuário escolher uma opção válida 
                      ou atingir o limite de tentativas. As tentativas são rastreadas por conversa.
                    </p>
                  </div>
                </>
              )}
            </CollapsibleContent>
          </Collapsible>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>
            Salvar Configuração
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <NodeHelpDialog 
      open={showHelp} 
      onOpenChange={setShowHelp} 
      nodeType={isList ? 'question_list' : 'question_buttons'} 
    />
    </>
  );
}
